<script setup lang="ts">
  defineOptions({ name: 'EventDetailTicketsSection' })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Download01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPButton from '@ui/components/CPButton.vue'
  import CPCard from '@ui/components/CPCard.vue'
  import CPTag from '@ui/components/CPTag.vue'
  import CPText from '@ui/components/CPText.vue'

  import EventDetailTabPanel from './EventDetailTabPanel.vue'

  import { useDirectoryPagination } from '../composables/useDirectoryPagination'
  import { isSafeTicketQrImageUrl } from '../utils/is-safe-ticket-qr-url'

  import type { EventDetailTicket } from '../types/event-detail'

  const props = defineProps({
    tickets: {
      type: Array as PropType<readonly EventDetailTicket[]>,
      required: true,
    },
    eventTitle: {
      type: String,
      required: true,
    },
    dateLabel: {
      type: String,
      default: '',
    },
    timeLabel: {
      type: String,
      default: '',
    },
    locationVenue: {
      type: String,
      default: '',
    },
    locationAddress: {
      type: String,
      default: '',
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    downloadTicket: [ticketId: string]
  }>()

  const PAGE_SIZE = 6

  const { t } = useI18n()

  const tickets = computed(() => [...props.tickets])

  const { pageItems, total, rangeStart, rangeEnd, canGoPrevious, canGoNext, goPrevious, goNext } = useDirectoryPagination(
    tickets,
    PAGE_SIZE
  )

  const subtitle = computed(() => {
    const count = props.tickets.length
    if (count === 1) return t('clientportal.events.detail.ticketsSubheadOne', { event: props.eventTitle })
    return t('clientportal.events.detail.ticketsSubheadMany', { count, event: props.eventTitle })
  })

  const footerLabel = computed(() =>
    t('clientportal.events.detail.pageRange', {
      start: rangeStart.value,
      end: rangeEnd.value,
      total: total.value,
    })
  )

  const qrImageUrl = (ticket: EventDetailTicket) => (isSafeTicketQrImageUrl(ticket.qrImageUrl) ? ticket.qrImageUrl : '')
</script>

<template>
  <div :id="createElementId(idPrefix, 'tickets', 'root')" class="mx-auto w-full max-w-[82.5rem] px-4 py-6 md:px-6">
    <EventDetailTabPanel
      :id-prefix="`${idPrefix}-tickets`"
      :title="t('clientportal.events.detail.ticketsTitle')"
      :subtitle="tickets.length === 0 ? '' : subtitle"
      :show-search="false"
      :footer-label="tickets.length === 0 ? '' : footerLabel"
      :show-pagination="tickets.length > PAGE_SIZE"
      :can-go-previous="canGoPrevious"
      :can-go-next="canGoNext"
      :previous-label="t('clientportal.events.detail.previous')"
      :next-label="t('clientportal.events.detail.next')"
      @previous="goPrevious"
      @next="goNext"
    >
      <div class="grid grid-cols-1 gap-4 p-4 md:p-6">
        <CPCard v-for="ticket in pageItems" :id="createElementId(idPrefix, 'ticket', ticket.id)" :key="ticket.id" class="overflow-hidden">
          <div :id="createElementId(idPrefix, 'ticket-layout', ticket.id)" class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_13rem]">
            <div :id="createElementId(idPrefix, 'ticket-details', ticket.id)" class="order-2 flex min-w-0 flex-col gap-5 p-5 md:order-1">
              <div class="flex items-start justify-between gap-3">
                <CPTag
                  :id="createElementId(idPrefix, 'ticket-pass', ticket.id)"
                  size="sm"
                  color="blue-light"
                  :bordered="false"
                  :interactive="false"
                  class="w-fit"
                >
                  <CPText size="sm" weight="bold">{{ ticket.ticketName }}</CPText>
                </CPTag>
                <CPButton
                  v-if="ticket.pdfUrl"
                  :id="createElementId(idPrefix, 'ticket-download', ticket.id)"
                  type="secondary"
                  color="secondary"
                  size="sm"
                  class="event-detail-ticket-icon-button"
                  :aria-label="t('clientportal.events.detail.ticketsDownloadPdf')"
                  @click="emit('downloadTicket', ticket.id)"
                >
                  <Download01Icon class="h-4 w-4" aria-hidden="true" />
                </CPButton>
              </div>

              <div class="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                <div v-if="dateLabel" :id="createElementId(idPrefix, 'ticket-date', ticket.id)">
                  <CPText size="md" weight="normal" class="uppercase tracking-[0.04em] text-500">
                    {{ t('clientportal.events.detail.ticketsFieldDate') }}
                  </CPText>
                  <CPText size="xl" weight="bold" html-tag="div" class="mt-1 text-body">{{ dateLabel }}</CPText>
                </div>
                <div v-if="timeLabel" :id="createElementId(idPrefix, 'ticket-time', ticket.id)">
                  <CPText size="md" weight="normal" class="uppercase tracking-[0.04em] text-500">
                    {{ t('clientportal.events.detail.ticketsFieldTime') }}
                  </CPText>
                  <CPText size="xl" weight="bold" html-tag="div" class="mt-1 text-body">{{ timeLabel }}</CPText>
                </div>
                <div v-if="locationVenue" :id="createElementId(idPrefix, 'ticket-location', ticket.id)">
                  <CPText size="md" weight="normal" class="uppercase tracking-[0.04em] text-500">
                    {{ t('clientportal.events.detail.ticketsFieldLocation') }}
                  </CPText>
                  <CPText size="xl" weight="bold" html-tag="div" class="mt-1 text-body">{{ locationVenue }}</CPText>
                  <CPText v-if="locationAddress" size="md" weight="normal" html-tag="div" class="mt-0.5 text-500">
                    {{ locationAddress }}
                  </CPText>
                </div>
                <div :id="createElementId(idPrefix, 'ticket-seats', ticket.id)">
                  <CPText size="md" weight="normal" class="uppercase tracking-[0.04em] text-500">
                    {{ t('clientportal.events.detail.ticketsFieldSeats') }}
                  </CPText>
                  <CPText size="xl" weight="bold" html-tag="div" class="mt-1 text-body">{{ ticket.seatCount }}</CPText>
                </div>
              </div>
            </div>

            <div
              :id="createElementId(idPrefix, 'ticket-qr-panel', ticket.id)"
              class="order-1 flex flex-col items-center justify-center border-b border-solid border-[var(--cp-border-500)] bg-[var(--cp-hover)] px-5 py-5 md:order-2 md:border-b-0 md:border-l"
            >
              <OptimisedImage
                v-if="qrImageUrl(ticket)"
                :id="createElementId(idPrefix, 'ticket-qr', ticket.id)"
                :src="qrImageUrl(ticket)"
                :alt="t('clientportal.events.detail.ticketsQrAria')"
                source="communities"
                :use-raw-url="true"
                class="h-32 w-32 overflow-hidden rounded-md bg-background"
                img-class="h-32 w-32 object-contain"
                fallback-aspect="aspect-square"
              />
              <CPText size="sm" weight="normal" class="mt-3 text-center text-500">
                {{ t('clientportal.events.detail.ticketsScan') }}
              </CPText>
            </div>
          </div>
        </CPCard>
      </div>

      <CPText v-if="pageItems.length === 0" size="md" weight="normal" class="px-4 pb-8 text-center text-500 md:px-6">
        {{ t('clientportal.events.detail.ticketsEmpty') }}
      </CPText>
    </EventDetailTabPanel>
  </div>
</template>

<style scoped>
  .event-detail-ticket-icon-button {
    --n-height: 2.25rem;
    --n-width: 2.25rem;
    --n-padding: 0;
    --n-border-radius: 0.5rem;
  }
</style>
